import {CSSProperties, useEffect, useMemo, useRef, useState} from 'react'
import {useVirtualizer, VirtualItem} from '@tanstack/react-virtual'
import CloseIcon from '@rsuite/icons/Close'
import SearchIcon from '@rsuite/icons/Search'
import Input from 'rsuite/Input'
import InputGroup from 'rsuite/InputGroup'
import Modal from 'rsuite/Modal'
import {
  SortTreeNode,
  moveItem,
  treeNodeMatchesQuery,
  updateNodeChildren,
} from './sortTree'

interface Props {
  isOpen?: boolean
  handleClose: () => void
  handleRefresh: () => void
  sortButtonName?: string
  saving?: boolean
  loading?: boolean
  loadError?: string | null
  onRetry: () => void
  tree: Array<SortTreeNode>
  onChange: (tree: Array<SortTreeNode>) => void
  onSubmit: (tree: Array<SortTreeNode>) => void
}

interface ExpandProps {
  expandedIds: Set<string>
  onToggle: (id: string) => void
  searchQuery: string
}

/**
 * Collapsed by default (children hidden) — with 500+ rows, always rendering
 * every nested level at once made the list unusably long. While the user is
 * searching, a branch auto-expands if a descendant (not necessarily the
 * branch itself) matches, so the actual hit is never hidden behind a
 * collapsed parent.
 */
const isNodeExpanded = (node: SortTreeNode, {expandedIds, searchQuery}: ExpandProps): boolean => {
  if (expandedIds.has(node.id)) return true
  if (!searchQuery.trim()) return false
  return node.children.some((child) => treeNodeMatchesQuery(child, searchQuery))
}

/** A row's own background varies by depth (see rowStyle); the toggle and
 * position input need colors that read clearly against any of them. */
const rowStyle = (depth: number): CSSProperties => ({
  display: 'flex',
  alignItems: 'center',
  width: `calc(100% - ${depth * 20}px)`,
  boxSizing: 'border-box',
  padding: '6px 10px',
  marginLeft: depth * 20,
  borderRadius: 4,
  border: '1px solid #dee1e6',
  background: depth === 0 ? '#ffffff' : '#f5f6f8',
  color: '#272c33',
  fontSize: 14,
})

const ExpandToggle = ({
  expanded,
  count,
  onToggle,
}: {
  expanded: boolean
  count: number
  onToggle: () => void
}) => (
  <span
    onClick={onToggle}
    style={{
      display: 'inline-block',
      minWidth: 44,
      marginRight: 8,
      cursor: 'pointer',
      color: '#6b7280',
      fontSize: 13,
    }}
    data-cy='sort-row-toggle'
  >
    {expanded ? '▾' : '▸'} ({count})
  </span>
)

/**
 * Editable 1-based position among this row's own siblings. Committing on
 * blur/Enter (not on every keystroke) avoids reordering mid-typing on a
 * multi-digit number. `position`/`max` are always the row's true index among
 * ALL siblings, regardless of the current search filter — typing "3" always
 * means "make this the 3rd item overall," not 3rd among filtered matches.
 */
const PositionInput = ({
  position,
  max,
  onMove,
}: {
  position: number
  max: number
  onMove: (newPosition: number) => void
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  const [draft, setDraft] = useState(String(position))

  // Any move — this row's own, or another row's that shifted this one —
  // changes the `position` prop; keep the field in sync unless it's what
  // the user is actively typing into right now.
  useEffect(() => {
    if (document.activeElement !== inputRef.current) setDraft(String(position))
  }, [position])

  const commit = () => {
    const parsed = parseInt(draft, 10)
    if (!Number.isNaN(parsed) && parsed !== position) {
      onMove(Math.min(Math.max(parsed, 1), max))
    } else {
      setDraft(String(position))
    }
  }

  return (
    <input
      ref={inputRef}
      type='number'
      min={1}
      max={max}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      onClick={(e) => e.stopPropagation()}
      onFocus={(e) => e.target.select()}
      style={{
        width: 52,
        marginRight: 10,
        padding: '2px 4px',
        textAlign: 'center',
        border: '1px solid #c7cad1',
        borderRadius: 4,
        background: '#ffffff',
        color: '#272c33',
      }}
      data-cy='sort-position-input'
    />
  )
}

const NestedSortableRow = ({
  node,
  index,
  siblings,
  depth,
  onReorder,
  expandProps,
}: {
  node: SortTreeNode
  index: number
  siblings: Array<SortTreeNode>
  depth: number
  onReorder: (newSiblings: Array<SortTreeNode>) => void
  expandProps: ExpandProps
}) => {
  const hasChildren = node.children.length > 0
  const expanded = hasChildren && isNodeExpanded(node, expandProps)

  return (
    <div style={{marginBottom: 4}}>
      <div style={rowStyle(depth)} data-cy={`${node.name || node.title}-button`}>
        <PositionInput
          position={index + 1}
          max={siblings.length}
          onMove={(newPosition) => onReorder(moveItem(siblings, index, newPosition - 1))}
        />
        {hasChildren && (
          <ExpandToggle
            expanded={expanded}
            count={node.children.length}
            onToggle={() => expandProps.onToggle(node.id)}
          />
        )}
        <span style={{flex: 1}}>{node.name || node.title}</span>
      </div>
      {expanded ? (
        <NestedSortableLevel
          nodes={node.children}
          depth={depth + 1}
          onReorder={(newChildren) =>
            onReorder(
              siblings.map((sibling) =>
                sibling.id === node.id ? {...sibling, children: newChildren} : sibling
              )
            )
          }
          expandProps={expandProps}
        />
      ) : null}
    </div>
  )
}

const NestedSortableLevel = ({
  nodes,
  depth,
  onReorder,
  expandProps,
}: {
  nodes: Array<SortTreeNode>
  depth: number
  onReorder: (newNodes: Array<SortTreeNode>) => void
  expandProps: ExpandProps
}) => (
  <div style={{width: '100%'}}>
    {nodes.map((node, index) => (
      <NestedSortableRow
        key={node.id}
        node={node}
        index={index}
        siblings={nodes}
        depth={depth}
        onReorder={onReorder}
        expandProps={expandProps}
      />
    ))}
  </div>
)

const TopLevelRow = ({
  node,
  position,
  totalCount,
  virtualRow,
  measureElement,
  onMove,
  onChildrenReorder,
  expandProps,
}: {
  node: SortTreeNode
  position: number
  totalCount: number
  virtualRow: VirtualItem
  measureElement: (el: Element | null) => void
  onMove: (newPosition: number) => void
  onChildrenReorder: (newChildren: Array<SortTreeNode>) => void
  expandProps: ExpandProps
}) => {
  const hasChildren = node.children.length > 0
  const expanded = hasChildren && isNodeExpanded(node, expandProps)

  return (
    <div
      ref={measureElement}
      data-index={virtualRow.index}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      <div style={{marginBottom: 4}}>
        <div style={rowStyle(0)} data-cy={`${node.name || node.title}-button`}>
          <PositionInput position={position} max={totalCount} onMove={onMove} />
          {hasChildren && (
            <ExpandToggle
              expanded={expanded}
              count={node.children.length}
              onToggle={() => expandProps.onToggle(node.id)}
            />
          )}
          <span style={{flex: 1}}>{node.name || node.title}</span>
        </div>
        {expanded ? (
          <NestedSortableLevel
            nodes={node.children}
            depth={1}
            onReorder={onChildrenReorder}
            expandProps={expandProps}
          />
        ) : null}
      </div>
    </div>
  )
}

const VirtualizedSortModal = ({
  isOpen,
  handleClose,
  handleRefresh,
  sortButtonName,
  saving,
  loading,
  loadError,
  onRetry,
  tree,
  onChange,
  onSubmit,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const scrollRef = useRef<HTMLDivElement>(null)

  const toggleExpand = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const expandProps: ExpandProps = {expandedIds, onToggle: toggleExpand, searchQuery}

  const visibleTopLevel = useMemo(
    () => tree.filter((node) => treeNodeMatchesQuery(node, searchQuery)),
    [tree, searchQuery]
  )

  const virtualizer = useVirtualizer({
    count: visibleTopLevel.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 46,
    overscan: 8,
  })

  const handleTopLevelMove = (nodeId: string, newPosition: number) => {
    const fromIndex = tree.findIndex((n) => n.id === nodeId)
    if (fromIndex === -1) return

    const newTree = moveItem(tree, fromIndex, newPosition - 1)
    onChange(newTree)

    // scrollToIndex needs the moved node's index within the *visible*
    // (search-filtered) list, not the full tree, or it lands on the wrong row.
    const newVisibleIndex = newTree
      .filter((n) => treeNodeMatchesQuery(n, searchQuery))
      .findIndex((n) => n.id === nodeId)
    if (newVisibleIndex !== -1) {
      virtualizer.scrollToIndex(newVisibleIndex, {align: 'center'})
    }
  }

  return (
    <Modal
      backdrop='static'
      role='alertdialog'
      open={isOpen}
      size='sm'
      onClose={handleClose}
      enforceFocus={false}
      data-cy='modal'
    >
      <Modal.Header>
        <Modal.Title data-cy='modal-title'>{sortButtonName}</Modal.Title>
      </Modal.Header>
      <Modal.Body className='mb-3'>
        {loadError ? (
          <div className='text-center py-4'>
            <p className='text-danger mb-2'>{loadError}</p>
            <button className='btn btn-primary btn-sm' onClick={onRetry} data-cy='sort-retry-button'>
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className='text-center py-4' data-cy='sort-loading'>
            Loading all items…
          </div>
        ) : (
          <>
            <InputGroup inside className='mb-2'>
              <Input
                placeholder='Search'
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                data-cy='sort-search-field'
              />
              <InputGroup.Addon>
                {searchQuery ? (
                  <CloseIcon
                    style={{cursor: 'pointer'}}
                    onClick={() => setSearchQuery('')}
                    data-cy='sort-search-close-button'
                  />
                ) : (
                  <SearchIcon />
                )}
              </InputGroup.Addon>
            </InputGroup>

            <div ref={scrollRef} style={{height: '60vh', overflowY: 'auto'}}>
              <div style={{height: virtualizer.getTotalSize(), position: 'relative', width: '100%'}}>
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const node = visibleTopLevel[virtualRow.index]
                  const position = tree.findIndex((n) => n.id === node.id) + 1
                  return (
                    <TopLevelRow
                      key={node.id}
                      node={node}
                      position={position}
                      totalCount={tree.length}
                      virtualRow={virtualRow}
                      measureElement={virtualizer.measureElement}
                      onMove={(newPosition) => handleTopLevelMove(node.id, newPosition)}
                      onChildrenReorder={(newChildren) =>
                        onChange(updateNodeChildren(tree, node.id, newChildren))
                      }
                      expandProps={expandProps}
                    />
                  )
                })}
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button
          className={`btn btn-primary btn-sm ms-3 ${saving || loading || loadError ? 'disabled' : ''}`}
          disabled={!!(saving || loading || loadError)}
          onClick={() => onSubmit(tree)}
          data-cy='modal-submit'
        >
          Save
        </button>
        <button
          onClick={() => {
            handleClose()
            handleRefresh()
          }}
          className='btn btn-secondary btn-sm ms-3'
          data-cy='modal-cancel'
        >
          Cancel
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default VirtualizedSortModal
